/**
 * B-GTCC-UI1 — Cockpit content primitives, 1:1 against the class contract
 * in `cockpit-ui.css` (itself a verbatim port of
 * GTCC_REFERENCE_2026-09-01.html's content-area styles). Every prop here is
 * content/state only: no arbitrary style overrides, no inline hex/px — the
 * only inline styles used are `width: <pct>%` (bar/meter/disk fills, which
 * are inherently per-instance numeric data, not styling choices) and
 * `color: var(--<one-of-the-16-cockpit-tokens>)` (also reference-literal,
 * e.g. the Offsite-Prüfung "FEHLGESCHLAGEN" row and the Disk-Prognose
 * warning caption). Every one of the 16 tokens is defined in
 * `packages/design-system/src/tokens.css` under `[data-surface="gtcc"]`.
 */
import { useLocale } from "@genius-tracker/i18n/react";
import { useEffect, useState, type ReactNode } from "react";

export type CockpitTone = "ok" | "crit" | "warn" | "info" | "muted";

/**
 * One age scale for every GTCC freshness surface.  Keeping the unit decision
 * here prevents individual views from rendering large second counts such as
 * "16,200 s" while the reference uses compact operational units.
 */
export function formatFreshnessAge(seconds: number): string {
  const value = Math.max(0, Math.floor(seconds));
  if (value < 60) return `${value} s`;
  if (value < 3_600) return `${Math.floor(value / 60)} min`;
  if (value < 86_400) return `${Math.floor(value / 3_600)} h`;
  return `${Math.floor(value / 86_400)} d`;
}

/** `.mock-banner` — gallery/mockup-only banner strip. */
export function MockBanner({ children }: { readonly children: ReactNode }) {
  return <div className="mock-banner">{children}</div>;
}

/** `.fresh` / `.fresh.stale` — data-freshness badge used on tile/panel headers. */
export function FreshBadge({
  label,
  measuredAt,
  stale = false,
}: {
  readonly label?: string | undefined;
  readonly measuredAt?: string | Date | null | undefined;
  readonly stale?: boolean;
}) {
  const { t } = useLocale();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (measuredAt === undefined || measuredAt === null) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [measuredAt]);
  if (measuredAt === undefined)
    return <span className={stale ? "fresh stale" : "fresh"}>{label}</span>;
  const instant =
    measuredAt === null ? null : measuredAt instanceof Date ? measuredAt : new Date(measuredAt);
  const text =
    instant === null || Number.isNaN(instant.getTime())
      ? t("admin.gtcc.fresh.unmeasurable")
      : t("admin.gtcc.fresh.age", {
          age: formatFreshnessAge((now - instant.getTime()) / 1_000),
        });
  return <span className={stale ? "fresh stale" : "fresh"}>{text}</span>;
}

/** `.chip` / `.chip.env` — compact host/environment label. */
export function Chip({ label, env = false }: { readonly label: string; readonly env?: boolean }) {
  return <span className={env ? "chip env" : "chip"}>{label}</span>;
}

/** `.pill` / `.pill.crit` / `.pill.warn` — count badge (nav alert count, tile counts). */
export function Pill({
  label,
  tone,
}: {
  readonly label: string;
  readonly tone: Extract<CockpitTone, "crit" | "warn">;
}) {
  return <span className={`pill ${tone}`}>{label}</span>;
}

/** `.dot` — status indicator dot used at the start of `.row` lines and table cells. */
export function Dot({ tone }: { readonly tone: CockpitTone }) {
  return <span className={`dot ${tone}`} />;
}

/** `.tabs` / `.tab` / `.tab.active` / `.tab.tab-sm` — top-level and sub-level tab bars. */
export interface TabItem {
  readonly key: string;
  readonly label: string;
}
export function Tabs({
  items,
  activeKey,
  sm = false,
}: {
  readonly items: readonly TabItem[];
  readonly activeKey: string;
  readonly sm?: boolean;
}) {
  return (
    <div className="tabs">
      {items.map((item) => {
        const classes = ["tab"];
        if (item.key === activeKey) classes.push("active");
        if (sm) classes.push("tab-sm");
        return (
          <div key={item.key} className={classes.join(" ")}>
            {item.label}
          </div>
        );
      })}
    </div>
  );
}

/** `.tiles` — the KPI-tile grid container. */
export function TilesGrid({
  children,
  testId,
}: {
  readonly children: ReactNode;
  readonly testId?: string;
}) {
  return (
    <div className="tiles" data-testid={testId}>
      {children}
    </div>
  );
}

/** `.card` (+ tone) / `h3` / `.big` (+ `small`) / `.st` (+ tone) — one KPI tile. */
export interface TileProps {
  readonly tone: CockpitTone;
  readonly title: string;
  readonly badge?: ReactNode;
  readonly value: ReactNode;
  readonly valueSuffix?: string;
  readonly statusTone?: CockpitTone;
  readonly status?: ReactNode;
}
export function Tile({ tone, title, badge, value, valueSuffix, statusTone, status }: TileProps) {
  return (
    <div className={`card ${tone}`}>
      <h3>
        {title}
        {badge}
      </h3>
      <div className="big">
        {value}
        {valueSuffix !== undefined ? <small>{valueSuffix}</small> : null}
      </div>
      {status !== undefined ? <div className={`st ${statusTone ?? tone}`}>{status}</div> : null}
    </div>
  );
}

/** `.panel` (+ `.chartp`) / `h2` — the generic content panel. */
export interface PanelProps {
  readonly title: ReactNode;
  readonly badge?: ReactNode;
  readonly children: ReactNode;
  readonly chart?: boolean;
  readonly headingLevel?: "h2" | "h3";
  readonly testId?: string | undefined;
}
export function Panel({
  title,
  badge,
  children,
  chart = false,
  headingLevel = "h2",
  testId,
}: PanelProps) {
  const Heading = headingLevel;
  return (
    <div className={chart ? "panel chartp" : "panel"} data-testid={testId}>
      <Heading>
        {title}
        {badge}
      </Heading>
      {children}
    </div>
  );
}

/** `.panel.chartp` — a Panel sized for a flex-filling `svg.spark` chart. */
export function ChartPanel(props: Omit<PanelProps, "chart">) {
  return <Panel {...props} chart />;
}

/** `.tbl-wrap` + `table` — the horizontally-scrollable table wrapper. */
export function TableWrap({ children }: { readonly children: ReactNode }) {
  return (
    <div className="tbl-wrap">
      <table>{children}</table>
    </div>
  );
}

/** `.host` / `.host small` — the host/service name cell used in Cockpit tables. */
export function HostCell({
  name,
  detail,
}: {
  readonly name: ReactNode;
  readonly detail?: ReactNode;
}) {
  return (
    <td className="host">
      {name}
      {detail !== undefined ? <small>{detail}</small> : null}
    </td>
  );
}

/** `.row` — one status/metric line inside a `Panel`. */
export interface RowProps {
  readonly tone: CockpitTone;
  readonly children: ReactNode;
  readonly value?: ReactNode;
  readonly valueTone?: CockpitTone;
  readonly muted?: boolean;
}
export function Row({ tone, children, value, valueTone, muted = false }: RowProps) {
  const valueClass = muted ? "mono muted" : "mono";
  return (
    <div className="row">
      <Dot tone={tone} />
      {children}
      {value !== undefined ? (
        <span
          className={valueClass}
          style={valueTone ? { color: `var(--${valueTone})` } : undefined}
        >
          {value}
        </span>
      ) : null}
    </div>
  );
}

/** `.bar` (+ `.hot` / `.max`) — a single-value progress bar. */
export function Bar({
  pct,
  tone,
}: {
  readonly pct: number;
  readonly tone?: Extract<CockpitTone, "warn" | "crit">;
}) {
  const cls = tone === "warn" ? "bar hot" : tone === "crit" ? "bar max" : "bar";
  return (
    <div className={cls}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** `.meter` (`.cool` / `.violet` / `.hot`) / `.pct` / `.seg` — CPU/memory meter. */
export type MeterTone = "cool" | "violet" | "hot";
export function Meter({
  tone,
  pct,
  caption,
}: {
  readonly tone: MeterTone;
  readonly pct: number;
  readonly caption: string;
}) {
  return (
    <div className={`meter ${tone}`}>
      <span className="pct">{pct} %</span>
      <div className="seg">
        <i style={{ width: `${pct}%` }} />
      </div>
      <small>{caption}</small>
    </div>
  );
}

/** `.disk` (+ `.hot`) / `.lbl2` / `.track` — the §1.9 disk-forecast cell. */
export function DiskCell({
  pct,
  usedLabel,
  freeLabel,
  forecast,
  hot = false,
  forecastTone,
}: {
  readonly pct: number;
  readonly usedLabel: string;
  readonly freeLabel: string;
  readonly forecast: string;
  readonly hot?: boolean;
  readonly forecastTone?: CockpitTone;
}) {
  return (
    <div className={hot ? "disk hot" : "disk"}>
      <div className="lbl2">
        <span>{usedLabel}</span>
        <span>{freeLabel}</span>
      </div>
      <div className="track">
        <i style={{ width: `${pct}%` }} />
      </div>
      <small style={forecastTone ? { color: `var(--${forecastTone})` } : undefined}>
        {forecast}
      </small>
    </div>
  );
}

/** `.net` (`.up` / `.dn`) — the in/out network mini-readout used in host tables. */
export function NetCell({
  up,
  down,
  muted = false,
}: {
  readonly up?: string;
  readonly down?: string;
  readonly muted?: boolean;
}) {
  if (muted) return <td className="net muted">—</td>;
  return (
    <td className="net">
      <span className="up">{up}</span>
      <br />
      <span className="dn">{down}</span>
    </td>
  );
}

/** `.legend` — chart legend row; entries are either a swatch+label or a muted note. */
export type LegendEntry =
  | { readonly tone: string; readonly label: ReactNode }
  | { readonly note: ReactNode };
export function Legend({ items }: { readonly items: readonly LegendEntry[] }) {
  return (
    <div className="legend">
      {items.map((item, i) =>
        "note" in item ? (
          <span key={i} className="mono muted">
            {item.note}
          </span>
        ) : (
          <span key={i}>
            <b style={{ background: `var(--${item.tone})` }} />
            {item.label}
          </span>
        ),
      )}
    </div>
  );
}

/** `.grid-main` — the two-column (≈1.9fr/1fr) content layout. */
export function GridMain({ children }: { readonly children: ReactNode }) {
  return <div className="grid-main">{children}</div>;
}

/** `.duo` (+ `.trio`) — a two- or three-column panel row nested inside `GridMain`. */
export function Duo({
  trio = false,
  children,
}: {
  readonly trio?: boolean;
  readonly children: ReactNode;
}) {
  return <div className={trio ? "duo trio" : "duo"}>{children}</div>;
}

/** `.trigrid` — the three-column Historical Trends chart grid. */
export function TriGrid({ children }: { readonly children: ReactNode }) {
  return <div className="trigrid">{children}</div>;
}

/** `.grid-bottom` — the auto-fit bottom row (donut / synthetic checks / delivery / incidents). */
export function GridBottom({ children }: { readonly children: ReactNode }) {
  return <div className="grid-bottom">{children}</div>;
}

/** `.donut-wrap` — donut SVG + `.dlg` legend side-by-side layout. */
export function DonutWrap({ children }: { readonly children: ReactNode }) {
  return <div className="donut-wrap">{children}</div>;
}

/** `.dlg` — the donut's own swatch legend (distinct markup from `.legend`). */
export type DonutLegendEntry =
  | { readonly tone: string; readonly label: ReactNode }
  | { readonly note: ReactNode };
export function DonutLegend({ items }: { readonly items: readonly DonutLegendEntry[] }) {
  return (
    <div className="dlg">
      {items.map((item, i) =>
        "note" in item ? (
          <span key={i} className="mono muted">
            {item.note}
          </span>
        ) : (
          <span key={i}>
            <b style={{ background: `var(--${item.tone})` }} />
            {item.label}
          </span>
        ),
      )}
    </div>
  );
}

/** `.decisions` — the decision-log section container (gallery-only, matches reference layout). */
export function DecisionsSection({
  title,
  intro,
  children,
}: {
  readonly title: ReactNode;
  readonly intro?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div className="decisions">
      <h2>{title}</h2>
      {intro !== undefined ? <div className="sub">{intro}</div> : null}
      {children}
    </div>
  );
}

/** `.dec` (+ `.approved`) / `.no` / `p` / `b` / `.q` — one decision card. */
export function DecisionCard({
  no,
  approved = false,
  children,
}: {
  readonly no: string;
  readonly approved?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className={approved ? "dec approved" : "dec"}>
      <span className="no">{no}</span>
      <p>{children}</p>
    </div>
  );
}

/** `.q` — the inline open-question fragment inside a `DecisionCard`. */
export function DecisionQuestion({ children }: { readonly children: ReactNode }) {
  return <span className="q">{children}</span>;
}
